# Each career has roles defined in
# https://github.com/teamniteo/handbook/blob/master/5_People/salary-system.md#roles
#
# For each role, we get the median US salary from Salary.com, which
# is annual amount in USD, we divide it by 12 and further divide it
# by the 10-year average of EUR -> USD exchange rate from ofx.com.
# We get a monthly base salary in EUR.
eur_to_usd_10_year_avg: 1.182208
careers_updated: '2026-01-10'
careers:

  - name: Design
    roles:
      - name: Junior Designer
        baseSalary: 5050
        salary_com_key: web-designer-i
      - name: Designer
        baseSalary: 5790
        salary_com_key: web-designer-ii
      - name: Senior Designer
        baseSalary: 7095
        salary_com_key: web-designer-iii
      - name: Lead Designer
        baseSalary: 9558
        salary_com_key: web-designer-iv
      - name: Principal Designer
        baseSalary: 10685
        salary_com_key: art-director
  - name: Marketing
    roles:
      - name: Junior Digital Marketer
        baseSalary: 4235
        salary_com_key: digital-marketing-analyst-i
      - name: Digital Marketer
        baseSalary: 4981
        salary_com_key: digital-marketing-analyst-ii
      - name: Senior Digital Marketer
        baseSalary: 7145
        salary_com_key: digital-marketing-analyst-iii
      - name: Lead Digital Marketer
        baseSalary: 8126
        salary_com_key: digital-marketing-manager
      - name: Principal Digital Marketer
        baseSalary: 14336
        salary_com_key: digital-marketing-director
  - name: Operations
    roles:
      - name: Customer Support
        baseSalary: 3519
        salary_com_key: customer-support-specialist
      - name: Technical Support
        baseSalary: 4442
        salary_com_key: technical-customer-support-specialist-i
      - name: Support Manager
        baseSalary: 5833
        salary_com_key: it-project-manager-i
      - name: Project Manager
        baseSalary: 6970
        salary_com_key: it-project-manager-ii
      - name: Operations Manager
        baseSalary: 8506
        salary_com_key: it-project-manager-iii
  - name: Technical
    roles:
      - name: Junior Software Engineer
        baseSalary: 5756
        salary_com_key: software-engineer-i
      - name: Software Engineer
        baseSalary: 7407
        salary_com_key: software-engineer-ii
      - name: Senior Software Engineer
        baseSalary: 9072
        salary_com_key: software-engineer-iii
      - name: Lead Software Engineer
        baseSalary: 10497
        salary_com_key: software-engineer-iv
      - name: Principal Software Engineer
        baseSalary: 12065
        salary_com_key: software-engineer-v
      - name: Information Technology Director
        baseSalary: 14437
        salary_com_key: information-technology-director

# For each country we get the ratio between US and that country's Numbeo
# Cost of Living Plus Rent Index value. Example: if Country A
# is 30% cheaper based on Cost of Living Plus Rent Index, then
# this country's `cost_of_living` is 0.7.
#
# Then, we want to slightly underpay people in expensive
# locations and not-so-slightly overpay people in cheap locations:
#  - in cheap locations, a MacBook costs the same (if not more);
#  - people in cheap locations can easily work remotely for a
#    company in a more expensive location;
#  - by overpaying cheap locations, we get access to top talent there;
#  - by underpaying expensive locations our hiring is harder there so
#    the culture fit must be greater.
#
# Additionally, we cannot afford to pay US-level salaries, so we
# set set our "affordability" ratio here, that we use to decrease
# `cost_of_living` values, which effectively decreases our salaries.
# The value is set somewhat arbitrarily, to match our salaries before
# the introduction of the Salary Calculator in Niteo. We adjust the
# "affordability" ratio in January, based on the previous year's
# performance.
#
# Finally for each country, we "compress" the `cost_of_living` towards
# our `affordability`. This means that if `cost_of_living` is higher
# than `affordability`, we decrease `cost_of_living` by 2/3 of the
# difference. If `cost_of_living` is lower than `affordability`, we
# increase `cost_of_living` by 2/3 of the difference.
#
# See `countries` and `compress_towards_affordability` functions in
# https://github.com/teamniteo/salarycalc/blob/master/fetch_config_values.py
# for exact details on these calculations.

affordability: 0.35
countries_updated: '2026-01-10'
countries:

  - name: Albania
    cost_of_living: 0.56
    compressed_cost_of_living: 0.42
  - name: Algeria
    cost_of_living: 0.3
    compressed_cost_of_living: 0.33
  - name: Argentina
    cost_of_living: 0.5
    compressed_cost_of_living: 0.4
  - name: Armenia
    cost_of_living: 0.52
    compressed_cost_of_living: 0.41
  - name: Australia
    cost_of_living: 0.95
    compressed_cost_of_living: 0.55
  - name: Austria
    cost_of_living: 0.9
    compressed_cost_of_living: 0.53
  - name: Bangladesh
    cost_of_living: 0.25
    compressed_cost_of_living: 0.32
  - name: Belarus
    cost_of_living: 0.39
    compressed_cost_of_living: 0.36
  - name: Belgium
    cost_of_living: 0.86
    compressed_cost_of_living: 0.52
  - name: Bolivia
    cost_of_living: 0.34
    compressed_cost_of_living: 0.35
  - name: Bosnia And Herzegovina
    cost_of_living: 0.44
    compressed_cost_of_living: 0.38
  - name: Brazil
    cost_of_living: 0.38
    compressed_cost_of_living: 0.36
  - name: Bulgaria
    cost_of_living: 0.49
    compressed_cost_of_living: 0.4
  - name: Cambodia
    cost_of_living: 0.43
    compressed_cost_of_living: 0.38
  - name: Canada
    cost_of_living: 0.86
    compressed_cost_of_living: 0.52
  - name: Chile
    cost_of_living: 0.48
    compressed_cost_of_living: 0.39
  - name: Colombia
    cost_of_living: 0.41
    compressed_cost_of_living: 0.37
  - name: Costa Rica
    cost_of_living: 0.69
    compressed_cost_of_living: 0.46
  - name: Croatia
    cost_of_living: 0.66
    compressed_cost_of_living: 0.45
  - name: Cyprus
    cost_of_living: 0.79
    compressed_cost_of_living: 0.5
  - name: Czech Republic
    cost_of_living: 0.68
    compressed_cost_of_living: 0.46
  - name: Denmark
    cost_of_living: 1.0
    compressed_cost_of_living: 0.56
  - name: Dominican Republic
    cost_of_living: 0.5
    compressed_cost_of_living: 0.4
  - name: Ecuador
    cost_of_living: 0.37
    compressed_cost_of_living: 0.36
  - name: Egypt
    cost_of_living: 0.25
    compressed_cost_of_living: 0.32
  - name: El Salvador
    cost_of_living: 0.52
    compressed_cost_of_living: 0.41
  - name: Estonia
    cost_of_living: 0.72
    compressed_cost_of_living: 0.47
  - name: Ethiopia
    cost_of_living: 0.56
    compressed_cost_of_living: 0.42
  - name: Finland
    cost_of_living: 0.85
    compressed_cost_of_living: 0.52
  - name: France
    cost_of_living: 0.84
    compressed_cost_of_living: 0.51
  - name: Georgia
    cost_of_living: 0.42
    compressed_cost_of_living: 0.37
  - name: Germany
    cost_of_living: 0.87
    compressed_cost_of_living: 0.52
  - name: Ghana
    cost_of_living: 0.42
    compressed_cost_of_living: 0.37
  - name: Greece
    cost_of_living: 0.64
    compressed_cost_of_living: 0.45
  - name: Guatemala
    cost_of_living: 0.59
    compressed_cost_of_living: 0.43
  - name: Honduras
    cost_of_living: 0.46
    compressed_cost_of_living: 0.39
  - name: Hungary
    cost_of_living: 0.57
    compressed_cost_of_living: 0.42
  - name: Iceland
    cost_of_living: 1.35
    compressed_cost_of_living: 0.68
  - name: India
    cost_of_living: 0.22
    compressed_cost_of_living: 0.31
  - name: Indonesia
    cost_of_living: 0.33
    compressed_cost_of_living: 0.34
  - name: Ireland
    cost_of_living: 1.04
    compressed_cost_of_living: 0.58
  - name: Israel
    cost_of_living: 1.05
    compressed_cost_of_living: 0.58
  - name: Italy
    cost_of_living: 0.76
    compressed_cost_of_living: 0.49
  - name: Japan
    cost_of_living: 0.58
    compressed_cost_of_living: 0.43
  - name: Jordan
    cost_of_living: 0.45
    compressed_cost_of_living: 0.38
  - name: Kenya
    cost_of_living: 0.34
    compressed_cost_of_living: 0.35
  - name: Kuwait
    cost_of_living: 0.6
    compressed_cost_of_living: 0.43
  - name: Latvia
    cost_of_living: 0.61
    compressed_cost_of_living: 0.44
  - name: Lebanon
    cost_of_living: 0.53
    compressed_cost_of_living: 0.41
  - name: Libya
    cost_of_living: 0.22
    compressed_cost_of_living: 0.31
  - name: Lithuania
    cost_of_living: 0.62
    compressed_cost_of_living: 0.44
  - name: Luxembourg
    cost_of_living: 1.17
    compressed_cost_of_living: 0.62
  - name: Malaysia
    cost_of_living: 0.41
    compressed_cost_of_living: 0.37
  - name: Malta
    cost_of_living: 0.78
    compressed_cost_of_living: 0.49
  - name: Mexico
    cost_of_living: 0.56
    compressed_cost_of_living: 0.42
  - name: Moldova
    cost_of_living: 0.48
    compressed_cost_of_living: 0.39
  - name: Montenegro
    cost_of_living: 0.55
    compressed_cost_of_living: 0.42
  - name: Morocco
    cost_of_living: 0.37
    compressed_cost_of_living: 0.36
  - name: Namibia
    cost_of_living: 0.47
    compressed_cost_of_living: 0.39
  - name: Nepal
    cost_of_living: 0.25
    compressed_cost_of_living: 0.32
  - name: Netherlands
    cost_of_living: 1.03
    compressed_cost_of_living: 0.57
  - name: New Zealand
    cost_of_living: 0.8
    compressed_cost_of_living: 0.5
  - name: Nicaragua
    cost_of_living: 0.4
    compressed_cost_of_living: 0.37
  - name: Nigeria
    cost_of_living: 0.46
    compressed_cost_of_living: 0.39
  - name: North Macedonia
    cost_of_living: 0.41
    compressed_cost_of_living: 0.37
  - name: Norway
    cost_of_living: 1.06
    compressed_cost_of_living: 0.58
  - name: Oman
    cost_of_living: 0.54
    compressed_cost_of_living: 0.41
  - name: Pakistan
    cost_of_living: 0.22
    compressed_cost_of_living: 0.31
  - name: Palestine
    cost_of_living: 0.56
    compressed_cost_of_living: 0.42
  - name: Panama
    cost_of_living: 0.64
    compressed_cost_of_living: 0.45
  - name: Paraguay
    cost_of_living: 0.36
    compressed_cost_of_living: 0.35
  - name: Peru
    cost_of_living: 0.42
    compressed_cost_of_living: 0.37
  - name: Philippines
    cost_of_living: 0.36
    compressed_cost_of_living: 0.35
  - name: Poland
    cost_of_living: 0.61
    compressed_cost_of_living: 0.44
  - name: Portugal
    cost_of_living: 0.67
    compressed_cost_of_living: 0.46
  - name: Puerto Rico
    cost_of_living: 0.8
    compressed_cost_of_living: 0.5
  - name: Qatar
    cost_of_living: 0.81
    compressed_cost_of_living: 0.5
  - name: Romania
    cost_of_living: 0.49
    compressed_cost_of_living: 0.4
  - name: Russia
    cost_of_living: 0.45
    compressed_cost_of_living: 0.38
  - name: Rwanda
    cost_of_living: 0.35
    compressed_cost_of_living: 0.35
  - name: Senegal
    cost_of_living: 0.66
    compressed_cost_of_living: 0.45
  - name: Serbia
    cost_of_living: 0.52
    compressed_cost_of_living: 0.41
  - name: Singapore
    cost_of_living: 1.45
    compressed_cost_of_living: 0.71
  - name: Slovakia
    cost_of_living: 0.62
    compressed_cost_of_living: 0.44
  - name: Slovenia
    cost_of_living: 0.7
    compressed_cost_of_living: 0.47
  - name: South Africa
    cost_of_living: 0.47
    compressed_cost_of_living: 0.39
  - name: South Korea
    cost_of_living: 0.73
    compressed_cost_of_living: 0.48
  - name: Spain
    cost_of_living: 0.69
    compressed_cost_of_living: 0.46
  - name: Sri Lanka
    cost_of_living: 0.39
    compressed_cost_of_living: 0.36
  - name: Sweden
    cost_of_living: 0.86
    compressed_cost_of_living: 0.52
  - name: Switzerland
    cost_of_living: 1.49
    compressed_cost_of_living: 0.73
  - name: Syria
    cost_of_living: 0.29
    compressed_cost_of_living: 0.33
  - name: Taiwan
    cost_of_living: 0.6
    compressed_cost_of_living: 0.43
  - name: Tanzania
    cost_of_living: 0.33
    compressed_cost_of_living: 0.34
  - name: Thailand
    cost_of_living: 0.49
    compressed_cost_of_living: 0.4
  - name: Tunisia
    cost_of_living: 0.33
    compressed_cost_of_living: 0.34
  - name: Turkey
    cost_of_living: 0.49
    compressed_cost_of_living: 0.4
  - name: Ukraine
    cost_of_living: 0.34
    compressed_cost_of_living: 0.35
  - name: United Kingdom
    cost_of_living: 0.93
    compressed_cost_of_living: 0.54
  - name: United States
    cost_of_living: 1.0
    compressed_cost_of_living: 0.56
  - name: Uruguay
    cost_of_living: 0.67
    compressed_cost_of_living: 0.46
  - name: Venezuela
    cost_of_living: 0.43
    compressed_cost_of_living: 0.38
  - name: Vietnam
    cost_of_living: 0.34
    compressed_cost_of_living: 0.35
